import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy, Share2 } from 'lucide-react';
import {
  copyPrivateRoomCode,
  normalizeRoomCode,
  sharePrivateRoomInvite,
} from 'lib/private-room-invite';

interface PrivateRoomCodeActionsProps {
  roomCode: string;
  variantCode?: string;
  tableName?: string;
  /** large = post-create success panel; compact = table header chip */
  size?: 'large' | 'compact';
  className?: string;
}

export default function PrivateRoomCodeActions({
  roomCode,
  variantCode,
  tableName,
  size = 'large',
  className = '',
}: PrivateRoomCodeActionsProps) {
  const code = normalizeRoomCode(roomCode);
  const [copied, setCopied] = useState(false);
  const [shareHint, setShareHint] = useState('');
  const resetTimerRef = useRef<number | null>(null);

  const clearHintTimer = useCallback(() => {
    if (resetTimerRef.current != null) {
      window.clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearHintTimer(), [clearHintTimer]);

  const flashCopied = () => {
    setCopied(true);
    clearHintTimer();
    resetTimerRef.current = window.setTimeout(() => setCopied(false), 2000);
  };

  const flashShareHint = (text: string) => {
    setShareHint(text);
    clearHintTimer();
    resetTimerRef.current = window.setTimeout(() => setShareHint(''), 2500);
  };

  const onCopy = async () => {
    const ok = await copyPrivateRoomCode(code);
    if (ok) flashCopied();
    else flashShareHint('Could not copy — try again');
  };

  const onShare = async () => {
    const result = await sharePrivateRoomInvite({ roomCode: code, variantCode, tableName });
    if (result === 'shared') flashShareHint('Invite sent');
    else if (result === 'copied') flashShareHint('Invite copied to clipboard');
    else flashShareHint('Share unavailable — copy the code instead');
  };

  if (!code) return null;

  const isLarge = size === 'large';

  return (
    <div className={`private-room-code-actions ${isLarge ? 'private-room-code-actions--large' : 'private-room-code-actions--compact'} ${className}`}>
      <div className="private-room-modal__code-wrap">
        <div
          className={`private-room-modal__code-display ${isLarge ? 'private-room-modal__code-display--large private-room-modal__code-display--with-copy' : ''}`}
          aria-label={`Room code ${code}`}
        >
          <span className="private-room-modal__code-text">{code}</span>
          {isLarge && (
            <button
              type="button"
              className="private-room-modal__code-copy"
              onClick={() => void onCopy()}
              aria-label={copied ? 'Room code copied' : 'Copy room code'}
              title={copied ? 'Copied' : 'Copy code'}
            >
              {copied ? <Check size={18} strokeWidth={2.25} /> : <Copy size={18} strokeWidth={2.25} />}
            </button>
          )}
        </div>
      </div>

      {!isLarge && (
        <div className="private-room-code-actions__buttons">
          <button type="button" className="private-room-code-actions__btn" onClick={() => void onCopy()}>
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? 'Copied' : 'Copy code'}
          </button>
          <button type="button" className="private-room-code-actions__btn private-room-code-actions__btn--gold" onClick={() => void onShare()}>
            <Share2 size={16} />
            Share invite
          </button>
        </div>
      )}

      {shareHint ? <p className="private-room-code-actions__hint">{shareHint}</p> : null}
    </div>
  );
}
